import React from 'react';
import { makeStyles } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import cx from 'classnames';

import { MenuItem, Menu, Button, Grid, units, Typography } from '@ha/dsl';

export interface SelectOptionProps {
  value: string | number;
  title?: string | number | React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  dataTestLocator?: string;
}

interface Props {
  label?: string | React.ReactNode;
  selectedItem?: SelectOptionProps;
  items: SelectOptionProps[];
  onChange: (item: SelectOptionProps) => void;
  dataTestLocator?: string;
}

export const useStyles = makeStyles(theme => ({
  button: {
    borderBottom: units.border(1, 'solid', theme.palette.grey[400]),
    borderRadius: 0,
    padding: units.yx(0.2, 0),
  },
  buttonSelected: {
    borderBottom: units.border(1, 'solid', theme.palette.primary.main),
  },
  label: {
    textTransform: 'initial',
    justifyContent: 'space-between',
  },
  buttonLabel: {
    color: theme.palette.grey[400],
    whiteSpace: 'nowrap',
  },
  options: {
    paddingTop: units.rem(1),
    paddingBottom: units.rem(1),
    minWidth: units.rem(7.5),
  },
}));

const Select: React.FC<Props> = ({
  selectedItem,
  label,
  items,
  onChange,
  dataTestLocator,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleClick = React.useCallback(
    event => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    },
    [anchorEl],
  );

  return (
    <React.Fragment>
      <Button
        fullWidth
        variant="text"
        size="large"
        className={cx({
          [classes.button]: true,
          [classes.buttonSelected]: selectedItem,
        })}
        classes={{ label: classes.label }}
        onClick={handleClick}
        data-test-locator={dataTestLocator}
      >
        <Typography.Caption
          className={!selectedItem ? classes.buttonLabel : undefined}
        >
          <Grid container alignItems="center" wrap="nowrap">
            {selectedItem?.icon && selectedItem.icon}
            {selectedItem?.title || label}
          </Grid>
        </Typography.Caption>
        <ArrowDropDownIcon />
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClick={handleClick}
        elevation={1}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {items.map(item => (
          <MenuItem
            key={item.value}
            value={item.value}
            onClick={() => onChange(item)}
            data-test-locator={item.dataTestLocator || null}
            className={classes.options}
          >
            <Typography.Caption>
              <Grid container alignItems="center" wrap="nowrap">
                {item?.icon && item.icon}
                {item.title}
              </Grid>
            </Typography.Caption>
          </MenuItem>
        ))}
      </Menu>
    </React.Fragment>
  );
};

export default Select;
